<template>
    <ul class="deposit" data-test-id="wig-page--payments--history-table">
        <div
            v-for="(group, index) in groupedPayments"
            :key="`${index}-group`"
            class="deposit__group"
        >
            <li
                v-for="(
                    { amount, source, comment, created_at }, i
                ) in group.payments"
                :key="i"
                class="deposit__item"
            >
                <p class="deposit__date">
                    <DateCell :value="created_at"></DateCell>
                </p>
                <div class="deposit__payment-block">
                    <p class="deposit__amount">{{ amount }}</p>
                    <p class="deposit__extra">
                        {{ text(source) }}, {{ comment }}
                    </p>
                </div>
            </li>
            <v-btn
                v-if="group.payment_link"
                class="deposit__button"
                min-width="36"
                color="transparent"
                dark
                @click="openLink(group.payment_link)"
            >
                <span class="deposit__button-icon-bx">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-file-earmark-pdf deposit__button-icon"
                        viewBox="0 0 16 16"
                    >
                        <path
                            d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"
                        />
                        <path
                            d="M4.603 14.087a.8.8 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.7 7.7 0 0 1 1.482-.645 20 20 0 0 0 1.062-2.227 7.3 7.3 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a11 11 0 0 0 .98 1.686 5.8 5.8 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.86.86 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.7 5.7 0 0 1-.911-.95 11.7 11.7 0 0 0-1.997.406 11.3 11.3 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.8.8 0 0 1-.58.029m1.379-1.901q-.25.115-.459.238c-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361q.016.032.026.044l.035-.012c.137-.056.355-.235.635-.572a8 8 0 0 0 .45-.606m1.64-1.33a13 13 0 0 1 1.01-.193 12 12 0 0 1-.51-.858 21 21 0 0 1-.5 1.05zm2.446.45q.226.245.435.41c.24.19.407.253.498.256a.1.1 0 0 0 .07-.015.3.3 0 0 0 .094-.125.44.44 0 0 0 .059-.2.1.1 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a4 4 0 0 0-.612-.053zM8.078 7.8a7 7 0 0 0 .2-.828q.046-.282.038-.465a.6.6 0 0 0-.032-.198.5.5 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822q.036.167.09.346z"
                        />
                    </svg>
                </span>
            </v-btn>

            <hr class="deposit__divider" />
        </div>
    </ul>
</template>

<script>
import { mapGetters } from 'vuex'
import { getterTypes } from '@/store/modules/payments'
import languageMixin from '@/mixins/language.mixin'
import DateCell from '@/components/common/cells/date-cell.vue'
import regularToSnakeCase from '@/helpers/regular-to-snake-case'

export default {
    name: 'OrderDepositHistory',
    components: { DateCell },
    mixins: [languageMixin],
    computed: {
        ...mapGetters('payments', { getPayments: getterTypes.getPayments }),
        groupedPayments: function () {
            return this.groupPayments(this.getPayments)
        },
    },
    methods: {
        text(text) {
            return this.textContent[regularToSnakeCase(text)] || `*${text}`
        },
        openLink(link) {
            window.open(link, '_blank')
        },
        findPaymentLink: function (payments) {
            return (
                payments.find((p) => p.rivhit_document_link)
                    ?.rivhit_document_link || null
            )
        },
        transformGroupedToArray: function (grouped) {
            return Array.from(grouped, ([key, payments]) => {
                return {
                    key,
                    payment_link: this.findPaymentLink(payments),
                    payments,
                }
            })
        },
        groupPayments: function (payments) {
            const grouped = new Map()
            let lastTimeKey = null

            payments.forEach((payment) => {
                const { rivhit_document_id, created_at } = payment

                if (rivhit_document_id) {
                    if (!grouped.has(rivhit_document_id)) {
                        grouped.set(rivhit_document_id, [])
                    }

                    grouped.get(rivhit_document_id).push(payment)

                    return
                }

                const timeKey = created_at

                if (timeKey !== lastTimeKey) {
                    lastTimeKey = timeKey
                    grouped.set(timeKey, [])
                }

                grouped.get(lastTimeKey).push(payment)
            })

            return this.transformGroupedToArray(grouped)
        },
    },
}
</script>

<style scoped lang="scss">
.deposit {
    padding: 0 0 5px;

    &__item {
        display: flex;
        color: #878787;
        font-size: 12px;
        min-height: 32px;
        align-items: flex-start;

        p {
            margin-bottom: 0 !important;
        }
    }

    &__group {
        position: relative;
        padding-top: 8px;
    }

    &__payment-block {
        display: flex;
        gap: 5px;
    }

    &__date {
        min-width: 120px;
        span {
            color: #878787 !important;
        }
    }

    &__amount {
        min-width: 38px;
        margin-right: 4px;
        font-weight: 700;
        color: #000;
    }

    &__button {
        position: absolute;
        right: 0;
        top: 8px;
        background: transparent;
        width: 30px;
        height: 25px;

        @include for-hebrew {
            right: auto;
            left: 0;
        }
    }

    &__button-icon-bx {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    &__button-icon {
        width: 22px;
        height: 22px;
        color: red;
    }

    &__divider {
        margin-top: 8px;
        width: 100%;
        height: 1px;
        background: #ccc;
    }

    &__extra {
        margin-right: 42px;
    }
}
</style>
